// Phase NOTARY-FOCUS N3 — 77-province coverage table (shared TH/EN)
import {
  NOTARY_PROVINCES, COVERAGE_TIER_LABEL_TH, COVERAGE_TIER_LABEL_EN,
  type NotaryProvince,
} from "@/data/notary-coverage";
import { MapPin, Building2, Truck } from "lucide-react";

const TIER_ICON = {
  branch: Building2,
  agent: MapPin,
  remote: Truck,
} as const;

const TIER_RING = {
  branch: "ring-emerald-300 bg-emerald-50 text-emerald-800",
  agent: "ring-amber-300 bg-amber-50 text-amber-800",
  remote: "ring-slate-300 bg-slate-50 text-slate-700",
} as const;

interface Props {
  lang: "th" | "en";
}

export default function NotaryCoverageTable({ lang }: Props) {
  const labels = lang === "th" ? COVERAGE_TIER_LABEL_TH : COVERAGE_TIER_LABEL_EN;
  // group by region
  const groups = new Map<string, NotaryProvince[]>();
  for (const p of NOTARY_PROVINCES) {
    const key = lang === "th" ? p.region : p.regionEn;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(p);
  }

  return (
    <div className="space-y-8">
      {/* Tier legend */}
      <div className="flex flex-wrap gap-2 text-[12px]">
        {(["branch", "agent", "remote"] as const).map((t) => {
          const Icon = TIER_ICON[t];
          return (
            <span key={t} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 ring-1 ${TIER_RING[t]}`}>
              <Icon className="h-3.5 w-3.5" /> {labels[t]}
            </span>
          );
        })}
      </div>

      {Array.from(groups.entries()).map(([region, list]) => (
        <section key={region}>
          <h3 className="mb-3 font-display text-lg font-extrabold text-navy-deep">
            {region}{" "}
            <span className="text-sm font-normal text-slate-500">
              ({list.length} {lang === "th" ? "จังหวัด" : "provinces"})
            </span>
          </h3>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {list.map((p) => {
              const Icon = TIER_ICON[p.tier];
              const name = lang === "th" ? p.th : p.en;
              const note = lang === "th" ? p.noteTh : p.noteEn;
              return (
                <div
                  key={p.en}
                  className={`flex items-start gap-2.5 rounded-xl border border-slate-200 bg-white p-3 ring-1 ring-transparent transition hover:border-amber-300 ${
                    p.tier === "branch" ? "ring-emerald-200/60" : ""
                  }`}
                >
                  <span className={`mt-0.5 rounded-full p-1.5 ring-1 ${TIER_RING[p.tier]}`}>
                    <Icon className="h-3.5 w-3.5" />
                  </span>
                  <div className="min-w-0">
                    <div className="truncate font-semibold text-navy-deep">{name}</div>
                    <div className="text-[11px] text-slate-500">{labels[p.tier]}</div>
                    {note && <div className="mt-1 text-[11px] leading-snug text-slate-600">{note}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}
